// PipelineNewVersionModal — Reforma UX §6.8 / wireframe dlgNewPipeVer.
//
// "Nova versão do pipeline": nova versão · base (versão ativa) · o que muda.
// Cada versão é IMUTÁVEL — o config da versão ativa é reaproveitado sem
// mudanças (base) e POST /v1/pipelines/:id/versions publica a nova, que vira
// a versão ativa (backend move latest_version_id). As anteriores permanecem
// inalteradas no histórico.
//
// NOTA: a rota ativa no backend pós-restart — até lá o POST retorna 404 e a
// UI degrada com mensagem clara (sem persistir nada).

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { GitBranch } from 'lucide-react';
import { createPipelineVersion } from '@/lib/api-pipelines';

/** Mesmo formato que o backend valida (Zod `^\d+\.\d+\.\d+$`). */
const SEMVER_REGEX = /^\d+\.\d+\.\d+$/;

/**
 * Normaliza erro do POST /v1/pipelines/:id/versions pra mensagem de UI.
 * 404 = rota ainda não ativa no backend em produção (degrada com aviso
 * pós-restart); 409/422 = mensagem do backend (duplicada/config inválida).
 *
 * (Duplicada em PipelineStepEditModal — exportar função de arquivo de
 * componente viola react-refresh/only-export-components.)
 */
function versionRequestErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (/HTTP 404|not.?found|rota n[ãa]o encontrada/i.test(msg)) {
    return 'O endpoint de nova versão ainda não está ativo no backend — '
      + 'disponível após a atualização (restart) do backend. Nada foi alterado.';
  }
  return msg;
}

export interface PipelineNewVersionModalProps {
  open: boolean;
  onClose: () => void;
  pipelineSlug: string;
  /** id (uuid) do pipeline — alvo do POST /v1/pipelines/:id/versions. */
  pipelineId: string;
  /** Label da versão ativa (ex: "3.0.0") — base imutável da nova versão. */
  activeVersionLabel: string | null;
  /** Config canonical da versão ativa — vai sem mudanças como base. */
  activeConfig: Record<string, unknown> | undefined;
  /** Sucesso: parent toasta + refetch do detalhe (nova versão vira ativa). */
  onSaved: (message: string) => void;
}

/** Form interno — desmonta junto com o DialogContent (estado zera por abertura). */
function NewVersionForm({
  pipelineId,
  activeVersionLabel,
  activeConfig,
  onSaved,
  onClose,
}: {
  pipelineId: string;
  activeVersionLabel: string | null;
  activeConfig: Record<string, unknown> | undefined;
  onSaved: (message: string) => void;
  onClose: () => void;
}) {
  const [newVersion, setNewVersion] = useState('');
  const [changes, setChanges] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!activeConfig) {
      setError('Sem config da versão ativa carregado — não dá pra criar a nova versão.');
      return;
    }
    const version = newVersion.trim();
    if (version && !SEMVER_REGEX.test(version)) {
      setError('Versão deve ser semver no formato X.Y.Z (ex: 4.0.0) — ou deixe vazio pra bump automático de minor.');
      return;
    }
    const changelog = changes.trim();
    setSubmitting(true);
    setError(null);
    try {
      const res = await createPipelineVersion(pipelineId, {
        // Base = versão ativa, sem mudanças (versões são imutáveis).
        config: activeConfig,
        ...(version ? { version } : {}),
        ...(changelog ? { changelog } : {}),
      });
      onSaved(
        `Versão v${res.version} criada`
        + `${activeVersionLabel ? ` (base v${activeVersionLabel})` : ''}`
        + ' — ela agora é a versão ativa; as versões anteriores permanecem imutáveis no histórico.',
      );
      onClose();
    } catch (err) {
      setError(versionRequestErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="npv-version">Nova versão</Label>
            <Input
              id="npv-version"
              value={newVersion}
              onChange={(e) => setNewVersion(e.target.value)}
              placeholder="ex: 4.0.0 (vazio = bump de minor)"
            />
          </div>
          <Select
            label="Base"
            options={[{ value: 'active', label: 'versão ativa' }]}
            value="active"
            onChange={() => {}}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="npv-changes">O que muda</Label>
          <Textarea
            id="npv-changes"
            value={changes}
            onChange={(e) => setChanges(e.target.value)}
            rows={4}
            placeholder="Descreva a mudança desta versão (changelog). Cada versão é imutável; a nova vira a versão ativa."
          />
        </div>
        {error && (
          <p className="text-xs text-raios-danger-text bg-raios-danger/10 border border-raios-danger/30 rounded-md px-3 py-2">
            {error}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button variant="brand" onClick={() => void handleCreate()} disabled={submitting}>
          {submitting ? 'Criando versão…' : 'Criar versão'}
        </Button>
      </DialogFooter>
    </>
  );
}

export function PipelineNewVersionModal({
  open,
  onClose,
  pipelineSlug,
  pipelineId,
  activeVersionLabel,
  activeConfig,
  onSaved,
}: PipelineNewVersionModalProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="z-[450]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-raios-accent-text" />
            Nova versão do pipeline
          </DialogTitle>
          <DialogDescription>
            Pipeline: <span className="font-mono text-raios-text-secondary">{pipelineSlug}</span>
            {activeVersionLabel && (
              <>
                {' '}&bull; versão ativa{' '}
                <span className="font-mono text-raios-accent-text">v{activeVersionLabel}</span>
              </>
            )}
            {' '}&bull; cada versão é <strong>imutável</strong> — a nova vira a ativa.
          </DialogDescription>
        </DialogHeader>
        {open && (
          <NewVersionForm
            pipelineId={pipelineId}
            activeVersionLabel={activeVersionLabel}
            activeConfig={activeConfig}
            onSaved={onSaved}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
